import dayjs, { type Dayjs } from "@eisox/dayjs";

import type { NumericSegment, NumericSegmentType, ParsedFormat, Segment } from "./date-format.helper";

type SegmentValues = readonly (string | null)[];

const PLACEHOLDER_CHAR = "-";
const MERIDIEM_AM = "AM";
const MERIDIEM_PM = "PM";

/** Two-digit years follow the dayjs convention: 00-68 is 21st century, 69-99 is 20th. */
const CENTURY_PIVOT = 68;

const COMPOSE_FORMAT = "YYYY-M-D H:m:s";

const DAYS_PER_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const isLeapYear = (year: number): boolean => year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);

/** `undefined` outside the Gregorian range — mid-edit segments hold values like `"0000"` or `"00"`. */
const daysInMonth = (year: number, month: number): number | undefined => {
  if (year < 1) return undefined;
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  return DAYS_PER_MONTH[month - 1];
};

const segmentAt = (parsed: ParsedFormat, index: number): Segment => {
  const segment = parsed.segments[index];
  if (!segment) throw new RangeError(`DateField: no segment at index ${index}.`);
  return segment;
};

const toDisplay = (segment: Segment, value: string | null): string => value ?? PLACEHOLDER_CHAR.repeat(segment.length);

const toSegmentValues = (parsed: ParsedFormat, value: Dayjs | null): SegmentValues => {
  const localized = value?.locale(parsed.locale) ?? null;
  return parsed.segments.map(segment => localized?.format(segment.token) ?? null);
};

const readNumber = (parsed: ParsedFormat, values: SegmentValues, type: NumericSegmentType): number | null => {
  const index = parsed.indexByType.get(type);
  if (index === undefined) return null;

  const value = values[index];
  if (value == null) return null;

  const numeric = Number(value);
  if (type !== "year" || value.length !== 2) return numeric;
  return numeric <= CENTURY_PIVOT ? 2000 + numeric : 1900 + numeric;
};

/** Only the day depends on its neighbours; every other bound is static. */
const resolveMax = (parsed: ParsedFormat, values: SegmentValues, segment: NumericSegment, reference: Dayjs): number => {
  if (segment.type !== "day") return segment.max;

  const year = readNumber(parsed, values, "year") ?? reference.year();
  const month = readNumber(parsed, values, "month") ?? reference.month() + 1;
  return daysInMonth(year, month) ?? segment.max;
};

const readHour = (parsed: ParsedFormat, values: SegmentValues): number | null => {
  const hour24 = readNumber(parsed, values, "hour24");
  if (hour24 != null) return hour24;

  const hour12 = readNumber(parsed, values, "hour12");
  if (hour12 == null) return null;

  const meridiemIndex = parsed.indexByType.get("meridiem");
  if (meridiemIndex === undefined) return hour12;

  const meridiem = values[meridiemIndex];
  if (meridiem == null) return null;
  return (hour12 % 12) + (meridiem === MERIDIEM_PM ? 12 : 0);
};

const toValue = (parsed: ParsedFormat, values: SegmentValues, reference: Dayjs | null): Dayjs | null => {
  if (values.some(value => value == null)) return null;

  const base = reference ?? dayjs().startOf("day");
  const year = readNumber(parsed, values, "year") ?? base.year();
  const month = readNumber(parsed, values, "month") ?? base.month() + 1;
  const day = readNumber(parsed, values, "day") ?? base.date();
  const hour = readHour(parsed, values) ?? base.hour();
  const minute = readNumber(parsed, values, "minute") ?? base.minute();
  const second = readNumber(parsed, values, "second") ?? base.second();

  const composed = dayjs(`${year}-${month}-${day} ${hour}:${minute}:${second}`, COMPOSE_FORMAT, true);
  return composed.isValid() ? composed.millisecond(base.millisecond()) : null;
};

const pad = (segment: Segment, value: number): string => String(value).padStart(segment.length, "0");

/** Turning the month or the year can strand the day past the end of the new month. */
const clampDay = (parsed: ParsedFormat, values: SegmentValues, reference: Dayjs): SegmentValues => {
  const index = parsed.indexByType.get("day");
  if (index === undefined) return values;

  const day = values[index];
  const segment = parsed.segments[index];
  if (day == null || segment?.type !== "day") return values;

  const max = resolveMax(parsed, values, segment, reference);
  if (Number(day) <= max) return values;

  const next = [...values];
  next[index] = pad(segment, max);
  return next;
};

const spinSegment = (
  parsed: ParsedFormat,
  values: SegmentValues,
  index: number,
  delta: number,
  reference: Dayjs | null,
): SegmentValues => {
  const segment = segmentAt(parsed, index);
  const next = [...values];
  const base = reference ?? dayjs().startOf("day");

  if (segment.type === "meridiem") {
    next[index] = values[index] === MERIDIEM_PM ? MERIDIEM_AM : MERIDIEM_PM;
    return next;
  }

  const max = resolveMax(parsed, values, segment, base);
  // An empty segment starts from the reference, minus delta so the spin lands on it.
  const current = values[index] == null ? Number(base.format(segment.token)) - delta : Number(values[index]);
  const span = max - segment.min + 1;

  next[index] = pad(segment, segment.min + ((((current + delta - segment.min) % span) + span) % span));
  return segment.type === "month" || segment.type === "year" ? clampDay(parsed, next, base) : next;
};

interface TypedCharacter {
  readonly values: SegmentValues;
  readonly buffer: string;
  readonly complete: boolean;
}

const typeCharacter = (
  parsed: ParsedFormat,
  values: SegmentValues,
  index: number,
  buffer: string,
  character: string,
  reference: Dayjs | null,
): TypedCharacter | null => {
  const segment = segmentAt(parsed, index);
  const next = [...values];

  if (segment.type === "meridiem") {
    const meridiem = { a: MERIDIEM_AM, p: MERIDIEM_PM }[character.toLowerCase()];
    if (!meridiem) return null;

    next[index] = meridiem;
    return { values: next, buffer: "", complete: true };
  }

  if (!/^\d$/.test(character)) return null;

  const max = resolveMax(parsed, values, segment, reference ?? dayjs().startOf("day"));
  const grown = `${buffer}${character}`;
  // Overflowing restarts from the digit just typed, which always fits: every bound is >= 12.
  const candidate = Number(grown) > max ? character : grown;
  const numeric = Number(candidate);

  // A leading zero is not a value yet: `0` on a day segment waits for the next digit.
  const incomplete = numeric < segment.min;
  const complete = !incomplete && (candidate.length >= segment.length || numeric * 10 > max);

  next[index] = incomplete ? candidate.padStart(segment.length, "0") : pad(segment, numeric);
  return { values: next, buffer: complete ? "" : candidate, complete };
};

interface TypedSequence {
  readonly values: SegmentValues;
  readonly buffer: string;
  /** Segment the caret ends on — a completed segment hands over to the next one. */
  readonly index: number;
  readonly changed: boolean;
}

const typeSequence = (
  parsed: ParsedFormat,
  values: SegmentValues,
  index: number,
  buffer: string,
  characters: string,
  reference: Dayjs | null,
): TypedSequence => {
  let cursor = index;
  let current = values;
  let pending = buffer;
  let changed = false;

  for (const character of characters) {
    const typed = typeCharacter(parsed, current, cursor, pending, character, reference);
    if (!typed) continue;

    current = typed.values;
    changed = true;

    const advance = typed.complete && cursor < parsed.segments.length - 1;
    cursor = advance ? cursor + 1 : cursor;
    pending = advance ? "" : typed.buffer;
  }

  return { values: current, buffer: pending, index: cursor, changed };
};

const eraseCharacter = (
  parsed: ParsedFormat,
  values: SegmentValues,
  index: number,
  buffer: string,
): { values: SegmentValues; buffer: string } => {
  const segment = segmentAt(parsed, index);
  const remaining = buffer.slice(0, -1);
  const next = [...values];

  next[index] = remaining === "" ? null : remaining.padStart(segment.length, "0");
  return { values: next, buffer: remaining };
};

const clearSegment = (values: SegmentValues, index: number): SegmentValues => {
  const next = [...values];
  next[index] = null;
  return next;
};

interface EditorState {
  readonly values: SegmentValues;
  /** Timestamp of the last value this field emitted, to tell an echo from a real change. */
  readonly emittedKey: number | null;
  readonly receivedKey: number | null;
  readonly formatKey: string;
}

const toEditorState = (parsed: ParsedFormat, value: Dayjs | null): EditorState => {
  const valueKey = value?.valueOf() ?? null;
  return {
    values: toSegmentValues(parsed, value),
    emittedKey: valueKey,
    receivedKey: valueKey,
    formatKey: parsed.pattern,
  };
};

/**
 * `null` when the incoming props already match the state. A value the field just emitted is an
 * echo: re-deriving the segments there would undo an edit a parent chose to ignore.
 */
const syncEditorState = (state: EditorState, parsed: ParsedFormat, value: Dayjs | null): EditorState | null => {
  const valueKey = value?.valueOf() ?? null;
  const formatKey = parsed.pattern;
  if (state.formatKey === formatKey && state.receivedKey === valueKey) return null;

  const external = state.formatKey !== formatKey || state.emittedKey !== valueKey;
  return {
    values: external ? toSegmentValues(parsed, value) : state.values,
    emittedKey: state.emittedKey,
    receivedKey: valueKey,
    formatKey,
  };
};

export {
  clearSegment,
  daysInMonth,
  eraseCharacter,
  resolveMax,
  segmentAt,
  spinSegment,
  syncEditorState,
  toDisplay,
  toEditorState,
  toSegmentValues,
  toValue,
  typeSequence,
};
export type { EditorState, SegmentValues };
